/**
 * Passenger Categories Utilities
 * Provides utility functions for handling passenger types and categories
 */

import {
  PASSENGER_TYPE_ALIASES,
  PASSENGER_CATEGORY_TYPES,
  DEFAULT_PASSENGER_CATEGORY,
  OVERBOOKED_PASSENGER_CATEGORY,
  ORDERED_PASSENGER_CATEGORIES,
} from '../constants/passengerCategories';

/**
 * Gets the display-only category for result translations without changing provider identity.
 * @param {string} category - Actual passenger category.
 * @param {string} brand - Current tenant brand.
 * @param {string} language - Current translation language.
 * @returns {string} Category whose existing translation should label the result.
 */
export const getResultPassengerCategoryLabelType = (category, brand, language) =>
  language === 'pt-BR' &&
  ['expressosaoluiz', 'saritur'].includes(brand) &&
  ['special', 'wheelchair_handicap'].includes(category)
    ? 'disability'
    : category;

export const PCD_SLOT_CATEGORY_KEYS = new Set([
  PASSENGER_CATEGORY_TYPES.DISABILITY,
  PASSENGER_CATEGORY_TYPES.SPECIAL,
  PASSENGER_CATEGORY_TYPES.WHEELCHAIR_HANDICAP,
]);

const PCD_BUS_CATEGORY_FALLBACK_ORDER = [
  PASSENGER_CATEGORY_TYPES.DISABILITY,
  PASSENGER_CATEGORY_TYPES.FREE_PASS,
  PASSENGER_CATEGORY_TYPES.SPECIAL,
  PASSENGER_CATEGORY_TYPES.WHEELCHAIR_HANDICAP,
];

/**
 * Resolves a category by presence, without a general-fare or availability fallback.
 * @param {string} category - Requested passenger category.
 * @param {Array<{type: string}>} availablePassengerTypes - Categories on this trip or line.
 * @returns {string|null} Matching type using the passenger form's PCD priority.
 */
export const resolvePassengerCategory = (category, availablePassengerTypes = []) => {
  if (availablePassengerTypes.some((passengerType) => passengerType.type === category))
    return category;
  if (PCD_SLOT_CATEGORY_KEYS.has(category)) {
    const pcdFallback = PCD_BUS_CATEGORY_FALLBACK_ORDER.find((type) => {
      return availablePassengerTypes.some((passengerType) => passengerType.type === type);
    });
    return pcdFallback || null;
  }
  return null;
};

/**
 * Returns whether the active category control needs the selected passenger quantity.
 * @param {string} category - Requested passenger category.
 * @param {string} brand - Current tenant brand.
 * @returns {boolean} Whether available seats must meet the requested quantity.
 */
export const requiresPassengerCategoryQuantity = (category, brand) =>
  category === PASSENGER_CATEGORY_TYPES.INTERMUNICIPAL_FREE_PASS ||
  (brand === 'saritur' && category === PASSENGER_CATEGORY_TYPES.OLDER);

/**
 * Returns whether an active category filter must survive unavailable polling results.
 * @param {string} category - Requested passenger category.
 * @param {number} passengerCount - Requested passenger quantity.
 * @param {string} brand - Current tenant brand.
 * @returns {boolean} Whether the filter remains selected.
 */
export const shouldPreservePassengerCategoryFilter = (category, passengerCount, brand) =>
  passengerCount > 0 &&
  (PCD_SLOT_CATEGORY_KEYS.has(category) || requiresPassengerCategoryQuantity(category, brand));

/**
 * Passenger keys that live in Redux / URL but are NOT sent to the search endpoint
 * via `category_type`. These are collected on the search form and forwarded to
 * downstream forms (e.g. passenger details), not used to filter trips.
 */
const CATEGORY_TYPE_EXCLUDED_KEYS = new Set([PASSENGER_CATEGORY_TYPES.CHILD_IN_ARMS]);

/**
 * Gets fallback passenger type when no specific types are available.
 * Returns 'general' if available, otherwise 'oversold'.
 *
 * @param {Array} availablePassengerTypes - Available passenger types with availability info
 * @returns {Array<string>} Array with single fallback type
 */
const getFallbackPassengerType = (availablePassengerTypes = []) => {
  const hasGeneralAvailable = availablePassengerTypes.some(
    (pt) => pt.type === DEFAULT_PASSENGER_CATEGORY && pt.availability > 0,
  );
  return hasGeneralAvailable ? [DEFAULT_PASSENGER_CATEGORY] : [OVERBOOKED_PASSENGER_CATEGORY];
};

/**
 * Normalizes passenger selection by converting legacy/alternative names to canonical types.
 * Also consolidates duplicate types by summing their values.
 *
 * @param {Object} passengers - Passenger selection object (e.g., { adult: 1, student: 1 })
 * @returns {Object} Normalized passenger selection
 *
 * @example
 * normalizePassengerTypes({ adult: 1, senior: 1 })
 * // Returns: { general: 1, older: 1 }
 *
 * @example
 * normalizePassengerTypes({ adult: 1, general: 2 })
 * // Returns: { general: 3 } (summed duplicates)
 */
export const normalizePassengerTypes = (passengers) => {
  if (!passengers || typeof passengers !== 'object') {
    return {};
  }

  const normalized = {};

  Object.entries(passengers).forEach(([key, value]) => {
    const canonicalType = PASSENGER_TYPE_ALIASES[key] || key;

    // Sum values if the same canonical type appears multiple times
    if (normalized[canonicalType]) {
      normalized[canonicalType] += value;
    } else {
      normalized[canonicalType] = value;
    }
  });

  return normalized;
};

/**
 * Gets passenger types that should be considered for price calculation.
 * Considers searched passenger types and their availability in the trip.
 *
 * @param {Object} searchPassengers - Passengers searched by user
 * @param {Array} availablePassengerTypes - Available passenger types in trip with availability info
 * @param {boolean} categoryBasedPricing - AB test flag: if false, only use general/oversold categories
 * @returns {Array<string>} Array of passenger type strings to consider
 *
 * @example
 * getPassengerTypesForPricing(
 *   { general: 1, student: 1 },
 *   [
 *     { type: 'general', availability: 10 },
 *     { type: 'student', availability: 5 }
 *   ],
 *   true
 * )
 * // Returns: ['general', 'student']
 *
 * @example
 * getPassengerTypesForPricing(
 *   { general: 1, student: 1 },
 *   [
 *     { type: 'general', availability: 10 },
 *     { type: 'student', availability: 0 }  // No availability
 *   ],
 *   true
 * )
 * // Returns: ['general'] (student excluded due to no availability)
 *
 * @example
 * getPassengerTypesForPricing(
 *   { general: 1, student: 1 },
 *   [
 *     { type: 'general', availability: 10 },
 *     { type: 'student', availability: 5 }
 *   ],
 *   false
 * )
 * // Returns: ['general'] (only general/oversold when AB test is false)
 */
export const getPassengerTypesForPricing = (
  searchPassengers,
  availablePassengerTypes = [],
  categoryBasedPricing = false,
  halfPriceFallbackEnabled = false,
) => {
  // AB Test: If false, only return general or oversold categories
  if (categoryBasedPricing === false) {
    return getFallbackPassengerType(availablePassengerTypes);
  }

  // Normalize passenger types
  const normalizedPassengers = normalizePassengerTypes(searchPassengers);

  // If no search passengers, fallback to general or oversold
  if (!normalizedPassengers || Object.keys(normalizedPassengers).length === 0) {
    return getFallbackPassengerType(availablePassengerTypes);
  }

  const searchedTypes = Object.keys(normalizedPassengers).filter(
    (key) => normalizedPassengers[key] > 0,
  );

  // Half-price alternatives for types that may appear as 50% variants in some lines
  const HALF_PRICE_FALLBACKS = {
    older: 'older_half',
    disadvantaged_youth: 'disadvantaged_youth_half',
  };

  const hasGeneralAvailable = availablePassengerTypes.some(
    (pt) => pt.type === DEFAULT_PASSENGER_CATEGORY && pt.availability > 0,
  );

  /**
   * Checks whether a passenger type has seats available in the trip.
   *
   * @param {string} type - Passenger type key to check.
   * @returns {boolean} True when the type has availability greater than zero.
   */
  const hasAvailability = (type) =>
    availablePassengerTypes.some((pt) => pt.type === type && pt.availability > 0);

  /**
   * Resolves a searched passenger type to its best available match:
   * the type itself, then its half-price variant (e.g. older → older_half)
   * when the flag is on, then general.
   *
   * @param {string} type - Searched passenger type key.
   * @returns {string|undefined} The resolved type or undefined when none is available.
   */
  const resolveType = (type) => {
    if (hasAvailability(type)) return type;

    const halfType = halfPriceFallbackEnabled && HALF_PRICE_FALLBACKS[type];
    if (halfType && hasAvailability(halfType)) return halfType;

    if (hasGeneralAvailable) return DEFAULT_PASSENGER_CATEGORY;
  };

  const availableSearchedTypes = searchedTypes.map(resolveType).filter(Boolean);

  // If none available, fallback to general/oversold
  if (availableSearchedTypes.length === 0) {
    return getFallbackPassengerType(availablePassengerTypes);
  }

  return availableSearchedTypes;
};

/**
 * Checks whether the visible passenger pricing can show a provider discount.
 * Provider discounts only apply when the displayed category is general or overbooked.
 *
 * @param {Object} searchPassengers - Passengers searched by the user.
 * @param {Array} availablePassengerTypes - Passenger types available for the trip.
 * @param {boolean} categoryBasedPricing - Whether category-based pricing is enabled.
 * @param {boolean} halfPriceCategoryFallback - Whether half-price category fallback is enabled.
 * @returns {boolean} Whether the provider discount can be shown for the visible pricing.
 */
export const shouldShowProviderDiscountForSearch = (
  searchPassengers,
  availablePassengerTypes = [],
  categoryBasedPricing = false,
  halfPriceCategoryFallback = false,
) => {
  const pricingPassengerTypes = getPassengerTypesForPricing(
    searchPassengers,
    availablePassengerTypes,
    categoryBasedPricing,
    halfPriceCategoryFallback,
  );
  const visiblePricingTypes = [...new Set(pricingPassengerTypes)];

  return (
    visiblePricingTypes.length === 1 &&
    (visiblePricingTypes[0] === DEFAULT_PASSENGER_CATEGORY ||
      visiblePricingTypes[0] === OVERBOOKED_PASSENGER_CATEGORY)
  );
};

/**
 * Derives the `category_type` payload field from a passenger selection.
 * Returns canonical non-`general` keys joined by commas, in `ORDERED_PASSENGER_CATEGORIES`
 * order. Form-only keys listed in `CATEGORY_TYPE_EXCLUDED_KEYS` (e.g. lap child) are skipped.
 * Returns empty string when no eligible categories are selected; callers should omit the
 * `category_type` field when the result is empty.
 *
 * @param {Object} passengers - Passenger selection (e.g. { general: 1, disability: 1, older: 1 }).
 * @returns {string} Comma-joined category list, or empty string.
 *
 * @example
 * deriveCategoryType({ general: 1, disability: 1, older: 1 });
 * // => 'older,disability'
 *
 * @example
 * deriveCategoryType({ general: 1, child_in_arms: 1 });
 * // => '' (child_in_arms is form-only, not a search filter)
 *
 * @example
 * deriveCategoryType({ general: 2 });
 * // => ''
 */
export const deriveCategoryType = (passengers) => {
  const normalized = normalizePassengerTypes(passengers);
  return ORDERED_PASSENGER_CATEGORIES.filter(
    (key) =>
      key !== DEFAULT_PASSENGER_CATEGORY &&
      !CATEGORY_TYPE_EXCLUDED_KEYS.has(key) &&
      normalized[key] > 0,
  ).join(',');
};
